import {supabase} from '../supabase/supabase';

const getFunctionErrorMessage=async(error)=>{
  if (!error) return 'The Stripe request failed.';

  if (error.context && typeof error.context.json==='function') {
    try {
      const payload=await error.context.json();

      if (payload?.error) {
        return payload.error;
      }
    } catch {
      // Fall back to the SDK error message below.
    }
  }

  return error.message || 'The Stripe request failed.';
};

const invokeStripeFunction=async(functionName,body={})=>{
  const {data,error}=await supabase.functions.invoke(
    functionName,
    {body}
  );

  if (error) {
    throw new Error(await getFunctionErrorMessage(error));
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  return data;
};

export const verifySellerPlatformCheckoutEligibility=async(sellerId)=>{
  try {
    const result=await invokeStripeFunction(
      'stripe-seller-checkout-eligibility',
      {seller_id:sellerId}
    );

    if (result?.success!==true || result?.eligible!==true) {
      throw new Error(
        'Seller platform-payment eligibility could not be confirmed.'
      );
    }

    return result;
  } catch (error) {
    throw new Error(
      `Online checkout is unavailable: ${error.message || 'seller eligibility could not be verified.'} No order was created.`
    );
  }
};

export const startSellerActivationCheckout=(
  sellerId,
  returnPath='/seller-dashboard'
)=>invokeStripeFunction(
  'stripe-seller-activation',
  {
    seller_id:sellerId,
    return_path:returnPath
  }
);

export const confirmSellerActivationPayment=(
  sellerId,
  sessionId
)=>invokeStripeFunction(
  'stripe-seller-activation',
  {
    action:'confirm',
    seller_id:sellerId,
    session_id:sessionId
  }
);

export const startStripeSellerOnboarding=(
  sellerId,
  returnPath='/seller-dashboard'
)=>invokeStripeFunction(
  'stripe-seller-onboarding',
  {
    seller_id:sellerId,
    return_path:returnPath
  }
);

export const activateSellerPlatformPayments=(
  sellerId
)=>invokeStripeFunction(
  'stripe-seller-payment-enable',
  {
    seller_id:sellerId
  }
);

export const syncStripeSellerOnboardingStatus=async(
  sellerId
)=>{
  try {
    return await activateSellerPlatformPayments(sellerId);
  } catch (error) {
    const message=error?.message || '';

    if (
      message.includes('not complete')
      || message.includes('Finish all required KYC')
      || message.includes('not eligible')
    ) {
      return {
        success:false,
        pending:true,
        stripe_onboarding_complete:false,
        platform_payments_enabled:false
      };
    }

    throw error;
  }
};

export const createOrderCheckout=async(
  orderId,
  returnPath='/orders'
)=>{
  try {
    return await invokeStripeFunction(
      'stripe-order-checkout',
      {
        order_id:orderId,
        return_path:returnPath
      }
    );
  } catch (checkoutError) {
    try {
      const cancellation=await cancelOrderCheckout(orderId);

      if (cancellation?.success!==true) {
        throw new Error(
          cancellation?.error || 'The unpaid order could not be closed.'
        );
      }
    } catch (cancellationError) {
      console.error(
        'Unable to close unpaid checkout order:',
        cancellationError
      );

      const checkoutMessage=checkoutError?.message
        || 'Unable to start checkout.';
      throw new Error(
        `${checkoutMessage} We could not confirm cancellation of the unpaid order. It remains hidden from order lists until payment authorization or cancellation is confirmed.`
      );
    }

    throw checkoutError;
  }
};

export const reconcileStripeOrder=(
  orderId
)=>invokeStripeFunction(
  'stripe-order-reconcile',
  {
    order_id:orderId
  }
);

export const cancelOrderCheckout=(
  orderId
)=>invokeStripeFunction(
  'stripe-order-cancel',
  {
    order_id:orderId
  }
);

export const updateStripeOrderStatus=(
  orderId,
  status,
  metadata={}
)=>invokeStripeFunction(
  'stripe-order-status',
  {
    order_id:orderId,
    status,
    ...metadata
  }
);
